import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Form, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from "reactstrap";
import axios from "axios";
import "./QuestionnaireForm.css";
import Penilaian from "./verifikasi/Penilaian";
import DataDiri from "./verifikasi/DataDiri";
import Foto from "./verifikasi/Foto";
import { validateForm } from "./verifikasi/validation";
import { kecamatanDesa } from "./questionnaire/constants";
import Layout from "../layout/Layout";

export const Verifikasi = () => {
  const navigate = useNavigate();
  const searchContainerRef = useRef(null);
  
  const [fotoRumah, setFotoRumah] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);

  const [fotoKK, setFotoKK] = useState(null);
  const [imagePreviewKK, setImagePreviewKK] = useState(null);
  const [isPreviewingFotoKK, setIsPreviewingFotoKK] = useState(false);

  const [fotoSuratKepemilikanTanah, setFotoSuratKepemilikanTanah] = useState(null);
  const [imagePreviewSuratKepemilikanTanah, setImagePreviewSuratKepemilikanTanah] = useState(null);
  const [isPreviewingSuratKepemilikanTanah, setIsPreviewingSuratKepemilikanTanah] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [desaOptions, setDesaOptions] = useState([]);
  
  const [fotoRumahSaatIni, setFotoRumahSaatIni] = useState(null);
  const [imagePreviewRumahSaatIni, setImagePreviewRumahSaatIni] = useState(null);
  const [isPreviewingRumahSaatIni, setIsPreviewingRumahSaatIni] = useState(false);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [nomorKTPTerm, setNomorKTPTerm] = useState("");
  
  const [questionnaireOptions, setQuestionnaireOptions] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState("");
  const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
  
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  const [formData, setFormData] = useState({
    namaKepalaKeluarga: "",
    nomorKK: "",
    nik: "",
    alamat: "",
    kecamatan: "",
    desaKelurahan: "",
    rumahTinggalSendiri: "",
    statusKeluarga: "",
    usiaPernikahan: "",
    StatusKepemilikanTanah: "",
    apakahLokasiTanahSesuaiRTRW: "",
    tingkatPenghasilanKeluarga: "",
    jumlahPenghuniRumah: "",
    statusVerifikasi: "Sudah Diverifikasi",
    namaSurveyor: "",
    titikKoordinatRumah: "",
    manualTitikKoordinatRumah: "",
    catatan: "",
    tanggalVerifikasi: new Date().toISOString().split('T')[0], // Added default today's date
  });
  
  // ✅ Fetch surveyor name
  useEffect(() => {
    const fetchSurveyorName = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL}/me`, {
          withCredentials: true, 
        });
  
        if (response.status === 200) {
          setFormData((prevData) => ({
            ...prevData,
            namaSurveyor: response.data.username,
          }));
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Error mengambil nama surveyor:", error);
        navigate("/login");
      }
    };
    fetchSurveyorName();
  }, [navigate]);

  // ✅ Fetch questionnaires (untuk dropdown) — mode ringan, terbatas
  const fetchQuestionnaires = useCallback(async (ktp = "") => {
    setLoadingQuestionnaires(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_URL}/getquestionnaires`, {
        params: {
          searchKTP: ktp,
          light: true,
          limit: 20
        },
        withCredentials: true,
      });

      if (response.status === 200) {
        setQuestionnaireOptions(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
      setErrorMessage("Gagal memuat data questionnaire");
    } finally {
      setLoadingQuestionnaires(false);
    }
  }, []);

  // ✅ Search KTP function (untuk auto-fill) - FIXED with smart filtering
  const searchKTP = useCallback(async (ktp) => {
    if (!ktp || ktp.length < 3) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_URL}/getquestionnaires`, {
        params: {
          searchKTP: ktp,
          light: true,
          limit: 20
        },
        withCredentials: true,
      });

      if (response.status === 200) {
        const allResults = response.data.data || [];
        
        // ✅ SMART FILTERING: Prioritaskan exact match dan starts with
        const filteredResults = allResults
          .map(item => {
            const nik = item.nomorKTP || "";
            let priority = 3; // Default: contains
            
            if (nik === ktp) {
              priority = 0; // Exact match - highest priority
            } else if (nik.startsWith(ktp)) {
              priority = 1; // Starts with - second priority
            } else if (nik.includes(ktp)) {
              priority = 2; // Contains - third priority
            }
            
            return { ...item, priority };
          })
          .filter(item => item.priority < 3) // Hanya ambil yang relevan
          .sort((a, b) => {
            // Sort by priority first
            if (a.priority !== b.priority) {
              return a.priority - b.priority;
            }
            // Then by NIK length (shorter = more relevant)
            return a.nomorKTP.length - b.nomorKTP.length;
          })
          .slice(0, 10); // Limit to 10 results
        
        setSearchResults(filteredResults);
        setShowSearchDropdown(filteredResults.length > 0 && !selectedQuestionnaire);
        
        // Auto-fill jika exact match
        const exactMatch = filteredResults.find(item => item.nomorKTP === ktp);
        if (exactMatch) {
          handleAutoFill(exactMatch);
          setShowSearchDropdown(false);
        }
      }
    } catch (error) {
      console.error("Error searching KTP:", error);
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setIsSearching(false);
    }
  }, [selectedQuestionnaire]); // ✅ Added dependency

  // ✅ Handle auto-fill form
  const handleAutoFill = (selectedData) => {
    setFormData(prevData => ({
      ...prevData,
      namaKepalaKeluarga: selectedData.namaLengkapKK || "",
      nomorKK: selectedData.nomorKK || "",
      nik: selectedData.nomorKTP || "",
      alamat: selectedData.alamatRumah || "",
      kecamatan: selectedData.kecamatan || "",
      desaKelurahan: selectedData.desaKelurahan || "",
      titikKoordinatRumah: selectedData.titikKoordinatRumah || "",
      manualTitikKoordinatRumah: selectedData.manualTitikKoordinatRumah || "",
    }));
    
    setSelectedQuestionnaire(selectedData.id.toString());
    setNomorKTPTerm(selectedData.nomorKTP);
    setShowSearchDropdown(false); // ✅ Close dropdown setelah select
  };

  useEffect(() => {
    fetchQuestionnaires();
  }, [fetchQuestionnaires]);

  // ✅ Handle KTP search dengan debounce - FIXED
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // ✅ FIX: Jangan search jika sudah ada questionnaire yang dipilih
      if (!selectedQuestionnaire) {
        searchKTP(nomorKTPTerm);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [nomorKTPTerm, searchKTP, selectedQuestionnaire]); // ✅ Added selectedQuestionnaire

  // ✅ Handle questionnaire selection dari dropdown - FIXED
  const handleQuestionnaireSelect = (e) => {
    const questionnaireId = e.target.value;
    setSelectedQuestionnaire(questionnaireId);
    
    if (questionnaireId) {
      const selectedData = questionnaireOptions.find(q => q.id.toString() === questionnaireId);
      
      if (selectedData) {
        handleAutoFill(selectedData);
        setNomorKTPTerm(selectedData.nomorKTP || "");
        setShowSearchDropdown(false); // ✅ Close search dropdown
      }
    } else {
      // Clear form
      setFormData(prevData => ({
        ...prevData,
        namaKepalaKeluarga: "",
        nomorKK: "",
        nik: "",
        alamat: "",
        kecamatan: "",
        desaKelurahan: "",
        titikKoordinatRumah: "",
        manualTitikKoordinatRumah: "",
      }));
      setNomorKTPTerm("");
      setShowSearchDropdown(false); // ✅ Close dropdown
    }
  };

  // ✅ Handle selection from search dropdown - FIXED
  const handleSearchResultSelect = (selectedData) => {
    handleAutoFill(selectedData);
    setShowSearchDropdown(false); // ✅ Explicitly close dropdown
  };

  // ✅ Handle KTP input change - FIXED
  const handleKTPInputChange = (e) => {
    const value = e.target.value;
    setNomorKTPTerm(value);
    
    // ✅ FIX: Clear selected questionnaire when typing
    if (value !== formData.nik) {
      setSelectedQuestionnaire("");
    }
    
    if (!value) {
      setFormData(prevData => ({
        ...prevData,
        namaKepalaKeluarga: "",
        nomorKK: "",
        nik: "",
        alamat: "",
        kecamatan: "",
        desaKelurahan: "",
        titikKoordinatRumah: "",
        manualTitikKoordinatRumah: "",
      }));
      setSelectedQuestionnaire("");
      setShowSearchDropdown(false);
    }
  };

  useEffect(() => {
    setDesaOptions(kecamatanDesa[formData.kecamatan] || []);
  }, [formData.kecamatan]);

  const validateFormData = () => {
    const newErrors = validateForm(formData, fotoSuratKepemilikanTanah, fotoKK);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors).join("\n");
      window.alert(`Harap perbaiki kesalahan berikut:\n\n${errorMessages}`);
      return false;
    }
    return true;
  };

  // ✅ FIXED: Click outside handler using ref
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 800);
    };
    
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      let updatedData = {
        ...prevData,
        [name]: value,
        ...(name === "manualTitikKoordinatRumah" ? { titikKoordinatRumah: "" } : {}),
        ...(name === "titikKoordinatRumah" ? { manualTitikKoordinatRumah: "" } : {}),
      };
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFormData()) return;

    try {
      const BASE_URL = process.env.REACT_APP_URL;
      const formDataToSend = new FormData();

      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (fotoRumah) formDataToSend.append('fotoRumah', fotoRumah);
      if (fotoKK) formDataToSend.append('fotoKK', fotoKK);
      if (fotoSuratKepemilikanTanah) formDataToSend.append('fotoSuratKepemilikanTanah', fotoSuratKepemilikanTanah);
      if (fotoRumahSaatIni) formDataToSend.append('fotoRumahSaatIni', fotoRumahSaatIni);

      const response = await axios.post(`${BASE_URL}/createverifikasi`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (response.status === 201) {
        setSuccessMessage("Data verifikasi berhasil disimpan!");
        setModalOpen(true);
        setFormData({
          namaKepalaKeluarga: "",
          nomorKK: "",
          nik: "",
          alamat: "",
          kecamatan: "",
          desaKelurahan: "",
          rumahTinggalSendiri: "",
          statusKeluarga: "",
          usiaPernikahan: "",
          StatusKepemilikanTanah: "",
          apakahLokasiTanahSesuaiRTRW: "",
          tingkatPenghasilanKeluarga: "",
          jumlahPenghuniRumah: "",
          statusVerifikasi: "Sudah Diverifikasi",
          namaSurveyor: formData.namaSurveyor,
          titikKoordinatRumah: "",
          manualTitikKoordinatRumah: "",
          catatan: "",
          tanggalVerifikasi: new Date().toISOString().split('T')[0], // ✅ Reset to today
        });
        setSelectedQuestionnaire("");
        setNomorKTPTerm("");
        setFotoRumah(null);
        setImagePreviewKK(null);
        setIsPreviewingFotoKK(false);
        setFotoKK(null);
        setFotoRumahSaatIni(null);
        setImagePreview(null);
        setErrorMessage("");
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Gagal menyimpan data verifikasi.");
      setSuccessMessage("");
      setModalOpen(true);
    }
  };

  const handleGetCoordinates = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData((prevData) => ({
            ...prevData,
            titikKoordinatRumah: `${latitude}, ${longitude}`,
            manualTitikKoordinatRumah: "", 
          }));
        },
        (error) => {
          setErrorMessage("Error getting coordinates: " + error.message);
          setModalOpen(true); 
        }
      );
    } else {
      setErrorMessage("Geolocation is not supported by this browser.");
      setModalOpen(true); 
    }
  };

  return (
    <Layout>
      <Row className={`justify-content-center ${isMobile ? "mx-0" : ""}`} style={{ marginLeft: isMobile ? "0" : "280px" }}>
        <Form onSubmit={handleSubmit} sx={{ p: 3, maxWidth: "800px", marginLeft: { xs: 0, md: "280px" } }}>
          <h3>Validasi PB:</h3>
          <div className="home-logo-container">
            <img src="/images/logobaru.svg" alt="Logo Aplikasi" className="home-logo"/>
          </div>
          
          {/* ✅ FIXED: Added ref to container */}
          <FormGroup className="mb-4">
            <label className="form-label" htmlFor="searchKTP">Cari Nomor KTP:</label>
            <div ref={searchContainerRef} className="search-container" style={{ position: 'relative' }}>
              <input
                type="text"
                id="searchKTP"
                placeholder="Masukkan nomor KTP (minimal 3 digit)"
                value={nomorKTPTerm}
                onChange={handleKTPInputChange}
                className="form-control"
                style={{ 
                  backgroundColor: isSearching ? '#f8f9fa' : 'white',
                  borderColor: searchResults.length > 0 ? '#28a745' : '#ced4da'
                }}
              />
              
              {isSearching && (
                <div style={{ 
                  position: 'absolute', 
                  right: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  fontSize: '12px',
                  color: '#6c757d'
                }}>
                  Mencari...
                </div>
              )}
              
              {/* ✅ FIXED: Dropdown dengan priority indicator */}
              {showSearchDropdown && searchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ced4da',
                  borderTop: 'none',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {searchResults.map((result, index) => {
                    // ✅ Determine match type for visual indicator
                    const isExactMatch = result.nomorKTP === nomorKTPTerm;
                    const isStartsWith = result.nomorKTP.startsWith(nomorKTPTerm);
                    
                    return (
                      <div
                        key={result.id}
                        onClick={() => handleSearchResultSelect(result)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f8f9fa',
                          fontSize: '14px',
                          backgroundColor: isExactMatch ? '#e7f5e7' : 'white',
                          borderLeft: isExactMatch ? '3px solid #28a745' : 'none',
                          paddingLeft: isExactMatch ? '9px' : '12px'
                        }}
                        onMouseEnter={(e) => {
                          if (!isExactMatch) e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          if (!isExactMatch) e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontWeight: 'bold', color: '#495057' }}>
                            NIK: {result.nomorKTP}
                          </span>
                          {isExactMatch && (
                            <span style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              borderRadius: '3px',
                              fontWeight: 'bold'
                            }}>
                              EXACT
                            </span>
                          )}
                          {!isExactMatch && isStartsWith && (
                            <span style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              borderRadius: '3px'
                            }}>
                              MATCH
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#6c757d', fontSize: '12px', marginTop: '2px' }}>
                          {result.namaLengkapKK}
                        </div>
                        <div style={{ color: '#adb5bd', fontSize: '11px' }}>
                          {result.alamatRumah}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {nomorKTPTerm && !isSearching && !selectedQuestionnaire && (
              <small className={`form-text ${searchResults.length > 0 ? 'text-success' : 'text-muted'}`}>
                {searchResults.length > 0 
                  ? `${searchResults.length} data ditemukan. Klik untuk mengisi form otomatis.`
                  : 'Tidak ada data ditemukan.'
                }
              </small>
            )}
            
            {/* ✅ Show indicator when questionnaire is selected */}
            {selectedQuestionnaire && (
              <small className="form-text text-info">
                ✓ Data dari questionnaire terpilih
              </small>
            )}
          </FormGroup>

          <FormGroup className="mb-4">
            <Label for="questionnaireSelect">Atau Pilih dari Daftar Lengkap:</Label>
            <Input
              type="select"
              name="questionnaireSelect"
              id="questionnaireSelect"
              value={selectedQuestionnaire}
              onChange={handleQuestionnaireSelect}
              disabled={loadingQuestionnaires}
            >
              <option value="">
                {loadingQuestionnaires ? "Memuat data..." : "Pilih Questionnaire"}
              </option>
              {questionnaireOptions.map((questionnaire) => (
                <option key={questionnaire.id} value={questionnaire.id}>
                  NIK: {questionnaire.nomorKTP} - {questionnaire.namaLengkapKK}
                </option>
              ))}
            </Input>
            {questionnaireOptions.length === 0 && !loadingQuestionnaires && (
              <small className="text-muted">Tidak ada data questionnaire tersedia</small>
            )}
          </FormGroup>

        

          <div className="form-container">
            <div className="form-column">
              <DataDiri 
                formData={formData} 
                handleChange={handleChange} 
                errors={errors} 
                desaOptions={desaOptions}
                fotoKK={fotoKK}
                setFotoKK={setFotoKK}
                imagePreviewKK={imagePreviewKK}
                setImagePreviewKK={setImagePreviewKK}
                isPreviewingFotoKK={isPreviewingFotoKK}
                setIsPreviewingFotoKK={setIsPreviewingFotoKK}
              />
              <Penilaian 
                formData={formData} 
                handleChange={handleChange} 
                errors={errors}
                handleGetCoordinates={handleGetCoordinates}
                fotoSuratKepemilikanTanah={fotoSuratKepemilikanTanah}
                setFotoSuratKepemilikanTanah={setFotoSuratKepemilikanTanah}
                imagePreviewSuratKepemilikanTanah={imagePreviewSuratKepemilikanTanah}
                setImagePreviewSuratKepemilikanTanah={setImagePreviewSuratKepemilikanTanah}
                isPreviewingSuratKepemilikanTanah={isPreviewingSuratKepemilikanTanah}
                setIsPreviewingSuratKepemilikanTanah={setIsPreviewingSuratKepemilikanTanah}
                fotoRumahSaatIni={fotoRumahSaatIni}
                setFotoRumahSaatIni={setFotoRumahSaatIni}
                imagePreviewRumahSaatIni={imagePreviewRumahSaatIni}
                setImagePreviewRumahSaatIni={setImagePreviewRumahSaatIni}
                isPreviewingRumahSaatIni={isPreviewingRumahSaatIni}
                setIsPreviewingRumahSaatIni={setIsPreviewingRumahSaatIni}
              />
            </div>
            
            <div className="form-column">
              <Foto 
                fotoRumah={fotoRumah}
                setFotoRumah={setFotoRumah}
                imagePreview={imagePreview}
                setImagePreview={setImagePreview}
                isPreviewing={isPreviewing}
                setIsPreviewing={setIsPreviewing}
              />
              <FormGroup className="mt-3">
                <Label for="catatan">Catatan <span className="text-muted">(Opsional)</span></Label>
                <Input
                  type="textarea"
                  name="catatan"
                  id="catatan"
                  value={formData.catatan}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Masukkan catatan tambahan tentang verifikasi ini (jika ada)..."
                  style={{
                    resize: 'vertical',
                    minHeight: '100px'
                  }}
                />
                <small className="text-muted">
                  Catatan dapat berisi informasi tambahan seperti kondisi khusus, kendala, atau keterangan lain yang relevan.
                </small>
              </FormGroup>
                {/* ✅ ADDED: Tanggal Verifikasi Field */}
          <FormGroup className="mb-4">
            <Label for="tanggalVerifikasi">
              Tanggal Verifikasi <span className="text-danger">*</span>
            </Label>
            <Input
              type="date"
              name="tanggalVerifikasi"
              id="tanggalVerifikasi"
              value={formData.tanggalVerifikasi}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]} // Prevent future dates
              required
            />
            <small className="text-muted">
              Tanggal ini akan digunakan sebagai tanggal pembuatan data (createdAt)
            </small>
          </FormGroup>

              <div className="mt-3 d-grid gap-2 d-md-flex justify-content-md-start">
                <Button className="btn-primary" type="submit">
                  Simpan Verifikasi
                </Button>
                <Button className="btn-secondary" type="button" onClick={() => navigate("/recapverifikasi")}>
                  Lihat Rekap
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </Row>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {errorMessage ? "Error" : "Sukses"}
        </ModalHeader>
        <ModalBody>{errorMessage || successMessage}</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setModalOpen(!modalOpen);
            if (successMessage) {
              navigate('/recapverifikasi'); 
            }
          }}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Layout>
  );
};